import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, orderBy, getDoc, where } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import {
    KelasRecord, NewKelasData, AsramaRecord, NewAsramaData,
    MurobbiRecord, NewMurobbiData, TahunAjaranRecord, NewTahunAjaranData,
    Division, StructureMember
} from '../types';

export interface MasterDataContextType {
    loading: boolean;
    kelasRecords: KelasRecord[];
    asramaRecords: AsramaRecord[];
    murobbiRecords: MurobbiRecord[];
    tahunAjaranRecords: TahunAjaranRecord[];
    activeSemester: string;
    halaqohRecords: any[];
    ekskulRecords: any[];
    divisions: Division[];
    structureMembers: StructureMember[];

    addKelas: (data: NewKelasData) => Promise<any>;
    updateKelas: (id: string, data: Partial<NewKelasData>) => Promise<void>;
    deleteKelas: (id: string) => Promise<void>;
    deleteMultipleKelas: (ids: string[]) => Promise<void>;

    addAsrama: (data: NewAsramaData) => Promise<any>;
    updateAsrama: (id: string, data: Partial<NewAsramaData>) => Promise<void>;
    deleteAsrama: (id: string) => Promise<void>;
    addMultipleAsrama: (asramaData: NewAsramaData[]) => Promise<void>;
    deleteMultipleAsrama: (ids: string[]) => Promise<void>;

    addMurobbi: (data: NewMurobbiData) => Promise<any>;
    updateMurobbi: (id: string, data: Partial<NewMurobbiData>) => Promise<void>;
    deleteMurobbi: (id: string) => Promise<void>;
    deleteMultipleMurobbi: (ids: string[]) => Promise<void>;


    addTahunAjaran: (data: NewTahunAjaranData) => Promise<any>;
    updateTahunAjaran: (id: string, data: Partial<NewTahunAjaranData>) => Promise<void>;
    deleteTahunAjaran: (id: string) => Promise<void>;
    activateTahunAjaran: (id: string) => Promise<void>;

    fetchTahunAjaranData: () => Promise<void>;
    fetchKelasData: () => Promise<void>;
    fetchAsramaData: () => Promise<void>;
    fetchMurobbiData: () => Promise<void>;
    fetchHalaqohData: () => Promise<void>;
    fetchEkskulData: () => Promise<void>;
    fetchDivisionsData: () => Promise<void>;
    fetchStructureData: () => Promise<void>;
}


export const MasterDataContext = createContext<MasterDataContextType | undefined>(undefined);

export const useMasterData = () => {
    const context = useContext(MasterDataContext);
    if (context === undefined) {
        throw new Error('useMasterData must be used within a MasterDataProvider');
    }
    return context;
};

export const MasterDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();

    const [kelasRecords, setKelasRecords] = useState<KelasRecord[]>([]);
    const [asramaRecords, setAsramaRecords] = useState<AsramaRecord[]>([]);
    const [murobbiRecords, setMurobbiRecords] = useState<MurobbiRecord[]>([]);
    const [tahunAjaranRecords, setTahunAjaranRecords] = useState<TahunAjaranRecord[]>([]);
    const [halaqohRecords, setHalaqohRecords] = useState<any[]>([]);
    const [ekskulRecords, setEkskulRecords] = useState<any[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [structureMembers, setStructureMembers] = useState<StructureMember[]>([]);
    const [loading, setLoading] = useState(true);

    const [hasFetchedKelas, setHasFetchedKelas] = useState(false);
    const [hasFetchedAsrama, setHasFetchedAsrama] = useState(false);
    const [hasFetchedMurobbi, setHasFetchedMurobbi] = useState(false);
    const [hasFetchedHalaqoh, setHasFetchedHalaqoh] = useState(false);
    const [hasFetchedEkskul, setHasFetchedEkskul] = useState(false);
    const [hasFetchedDivisions, setHasFetchedDivisions] = useState(false);
    const [hasFetchedStructure, setHasFetchedStructure] = useState(false);

    const activeSemester = React.useMemo(() => {

        const month = new Date().getMonth() + 1; // 1-12
        return (month >= 7 && month <= 12) ? 'Ganjil' : 'Genap';
    }, []);

    useEffect(() => {
        if (!authUser) {
            setKelasRecords([]);
            setAsramaRecords([]);
            setMurobbiRecords([]);
            setTahunAjaranRecords([]);
            setHalaqohRecords([]);
            setEkskulRecords([]);
            setDivisions([]);
            setStructureMembers([]);

            setHasFetchedKelas(false);
            setHasFetchedAsrama(false);
            setHasFetchedMurobbi(false);
            setHasFetchedHalaqoh(false);
            setHasFetchedEkskul(false);
            setHasFetchedDivisions(false);
            setHasFetchedStructure(false);

            setLoading(false);

            return;
        }


        setLoading(false);

    }, [authUser]);


    const fetchTahunAjaranData = async () => {
        if (!authUser) return;
        if (tahunAjaranRecords.length > 0) return; // cache
        try {
            const snapshot = await getDocs(query(collection(db, 'tahun_ajaran'), orderBy('tanggalMulai', 'desc')));
            const taData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    tanggalMulai: data.tanggalMulai?.toDate?.() || data.tanggalMulai,
                    tanggalAkhir: data.tanggalAkhir?.toDate?.() || data.tanggalSelesai?.toDate?.() || data.tanggalAkhir,
                } as TahunAjaranRecord;
            });
            setTahunAjaranRecords(taData);
        } catch (e) { console.error(e); }
    };

    const fetchKelasData = async () => {
        if (hasFetchedKelas) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'kelas'), orderBy('namaKelas', 'asc')));
            setKelasRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as KelasRecord)));
            setHasFetchedKelas(true);
        } catch (e) { console.error(e); }
    };

    const fetchAsramaData = async () => {
        if (hasFetchedAsrama) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'asrama'), orderBy('namaAsrama', 'asc')));
            setAsramaRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AsramaRecord)));
            setHasFetchedAsrama(true);
        } catch (e) { console.error(e); }
    };

    const fetchMurobbiData = async () => {
        if (hasFetchedMurobbi) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'murobbi'), orderBy('nama', 'asc')));
            setMurobbiRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MurobbiRecord)));
            setHasFetchedMurobbi(true);
        } catch (e) { console.error(e); }
    };

    const fetchDivisionsData = async () => {
        if (hasFetchedDivisions) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'divisions'), orderBy('name', 'asc')));
            setDivisions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Division)));
            setHasFetchedDivisions(true);
        } catch (e) { console.error(e); }
    };

    const fetchStructureData = async () => {
        if (hasFetchedStructure) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'structure_members')));
            setStructureMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StructureMember)));
            setHasFetchedStructure(true);
        } catch (e) { console.error(e); }
    };

    const fetchHalaqohData = async () => {
        if (!authUser) return;
        if (hasFetchedHalaqoh && halaqohRecords.length > 0) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'halaqoh')));
            setHalaqohRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setHasFetchedHalaqoh(true);
        } catch (e) { console.error(e); }
    };

    const fetchEkskulData = async () => {
        if (!authUser) return;
        if (hasFetchedEkskul && ekskulRecords.length > 0) return;
        try {
            const snapshot = await getDocs(query(collection(db, 'ekstrakurikuler')));
            setEkskulRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setHasFetchedEkskul(true);
        } catch (e) { console.error(e); }
    };


    // Implementation for Kelas
    // Perubahan kelas langsung diterapkan ke state lokal, supaya halaman (mis. Portal Guru → Kelola Kelas)
    // menampilkan hasilnya tanpa refresh. fetchKelasData hanya mengambil data sekali per sesi.
    const urutKelas = (list: KelasRecord[]) => [...list].sort((a, b) => (a.namaKelas || '').localeCompare(b.namaKelas || ''));
    const addKelas = async (data: NewKelasData) => {
        const ref = await addDoc(collection(db, 'kelas'), data);
        setKelasRecords(prev => urutKelas([...prev.filter(k => k.id !== ref.id), { id: ref.id, ...data } as KelasRecord]));
        return ref;
    };
    const updateKelas = async (id: string, data: Partial<NewKelasData>) => {
        const classRef = doc(db, 'kelas', id);
        const classSnap = await getDoc(classRef);
        if (!classSnap.exists()) return;
        const oldClassData = classSnap.data() as KelasRecord;
        const currentClassName = oldClassData.namaKelas;

        await updateDoc(classRef, data);
        setKelasRecords(prev => urutKelas(prev.map(k => k.id === id ? { ...k, ...data } as KelasRecord : k)));

        if (data.waliKelas && data.waliKelas !== oldClassData.waliKelas) {
            const q = query(collection(db, 'santri'), where('kelas', '==', currentClassName), where('status', '==', 'Aktif'));
            const santriSnap = await getDocs(q);
            if (!santriSnap.empty) {
                const batch = writeBatch(db);
                santriSnap.docs.forEach(doc => {
                    batch.update(doc.ref, { waliKelas: data.waliKelas });
                });
                await batch.commit();
            }
        }
    };
    const deleteKelas = async (id: string) => {
        await deleteDoc(doc(db, 'kelas', id));
        setKelasRecords(prev => prev.filter(k => k.id !== id));
    };
    const deleteMultipleKelas = async (ids: string[]) => {
        if (ids.length === 0) return;
        const batch = writeBatch(db);
        ids.forEach(id => batch.delete(doc(db, 'kelas', id)));
        await batch.commit();
        const hapus = new Set(ids);
        setKelasRecords(prev => prev.filter(k => !hapus.has(k.id)));
    };

    // Implementation for Asrama
    // Seperti kelas: perubahan langsung diterapkan ke state lokal supaya tampil tanpa refresh.
    const urutAsrama = (list: AsramaRecord[]) => [...list].sort((a, b) => (a.namaAsrama || '').localeCompare(b.namaAsrama || ''));
    const addAsrama = async (data: NewAsramaData) => {
        const ref = await addDoc(collection(db, 'asrama'), data);
        setAsramaRecords(prev => urutAsrama([...prev.filter(a => a.id !== ref.id), { id: ref.id, ...data } as AsramaRecord]));
        return ref;
    };
    const updateAsrama = async (id: string, data: Partial<NewAsramaData>) => {
        await updateDoc(doc(db, 'asrama', id), data);
        setAsramaRecords(prev => urutAsrama(prev.map(a => a.id === id ? { ...a, ...data } as AsramaRecord : a)));
    };
    const deleteAsrama = async (id: string) => {
        await deleteDoc(doc(db, 'asrama', id));
        setAsramaRecords(prev => prev.filter(a => a.id !== id));
    };
    const addMultipleAsrama = async (asramaData: NewAsramaData[]) => {
        const batch = writeBatch(db);
        const baru: AsramaRecord[] = [];
        asramaData.forEach(data => {
            const ref = doc(collection(db, 'asrama'));
            batch.set(ref, data);
            baru.push({ id: ref.id, ...data } as AsramaRecord);
        });
        await batch.commit();
        setAsramaRecords(prev => urutAsrama([...prev, ...baru]));
    };
    const deleteMultipleAsrama = async (ids: string[]) => {
        if (ids.length === 0) return;
        const batch = writeBatch(db);
        ids.forEach(id => batch.delete(doc(db, 'asrama', id)));
        await batch.commit();
        const hapus = new Set(ids);
        setAsramaRecords(prev => prev.filter(a => !hapus.has(a.id)));
    };

    // Implementation for Murobbi
    const urutMurobbi = (list: MurobbiRecord[]) => [...list].sort((a, b) => (a.nama || '').localeCompare(b.nama || ''));
    const addMurobbi = async (data: NewMurobbiData) => {
        const ref = await addDoc(collection(db, 'murobbi'), data);
        setMurobbiRecords(prev => urutMurobbi([...prev.filter(m => m.id !== ref.id), { id: ref.id, ...data } as MurobbiRecord]));
        return ref;
    };
    const updateMurobbi = async (id: string, data: Partial<NewMurobbiData>) => {
        const docRef = doc(db, 'murobbi', id);
        const snap = await getDoc(docRef);
        const oldData = snap.exists() ? snap.data() as MurobbiRecord : null;

        await updateDoc(docRef, data);
        setMurobbiRecords(prev => urutMurobbi(prev.map(m => m.id === id ? { ...m, ...data } as MurobbiRecord : m)));
        if (!oldData) return;

        const batch = writeBatch(db);
        let batchCount = 0;

        if (data.nama && data.nama !== oldData.nama) {
            const q = query(collection(db, 'santri'), where('murobbi', '==', oldData.nama));
            const santriSnap = await getDocs(q);
            if (!santriSnap.empty) {
                santriSnap.docs.forEach(doc => {
                    batch.update(doc.ref, { murobbi: data.nama });
                    batchCount++;
                });
            }
        }

        if (data.asramaBinaan && JSON.stringify(data.asramaBinaan) !== JSON.stringify(oldData.asramaBinaan)) {
            const oldAsramas = oldData.asramaBinaan || [];
            const newAsramas = data.asramaBinaan || [];
            const removedAsramas = oldAsramas.filter(a => !newAsramas.includes(a));

            for (const removedAsrama of removedAsramas) {
                const targetMurobbiName = oldData.nama;
                const q = query(collection(db, 'santri'), where('asrama', '==', removedAsrama), where('murobbi', '==', targetMurobbiName));
                const santriSnap = await getDocs(q);
                santriSnap.docs.forEach(doc => {
                    batch.update(doc.ref, { murobbi: '' });
                    batchCount++;
                });
            }

            const addedAsramas = newAsramas.filter(a => !oldAsramas.includes(a));
            for (const addedAsrama of addedAsramas) {
                const q = query(collection(db, 'santri'), where('asrama', '==', addedAsrama));
                const santriSnap = await getDocs(q);
                const newMurobbiName = data.nama || oldData.nama;
                santriSnap.docs.forEach(doc => {
                    batch.update(doc.ref, { murobbi: newMurobbiName });
                    batchCount++;
                });
            }
        }
        if (batchCount > 0) {
            await batch.commit();
        }
    };
    const deleteMurobbi = async (id: string) => {
        await deleteDoc(doc(db, 'murobbi', id));
        setMurobbiRecords(prev => prev.filter(m => m.id !== id));
    };
    const deleteMultipleMurobbi = async (ids: string[]) => {
        if (ids.length === 0) return;
        const batch = writeBatch(db);
        ids.forEach(id => batch.delete(doc(db, 'murobbi', id)));
        await batch.commit();
        const hapus = new Set(ids);
        setMurobbiRecords(prev => prev.filter(m => !hapus.has(m.id)));
    };

    // Implementation for Tahun Ajaran
    const addTahunAjaran = async (data: NewTahunAjaranData) => await addDoc(collection(db, 'tahun_ajaran'), data);
    const updateTahunAjaran = async (id: string, data: Partial<NewTahunAjaranData>) => await updateDoc(doc(db, 'tahun_ajaran', id), data);
    const deleteTahunAjaran = async (id: string) => await deleteDoc(doc(db, 'tahun_ajaran', id));
    const activateTahunAjaran = async (id: string) => {
        const batch = writeBatch(db);
        const snapshot = await getDocs(query(collection(db, 'tahun_ajaran')));
        snapshot.forEach(docSnap => {
            if (docSnap.id === id) {
                batch.update(docSnap.ref, { status: 'Aktif' });
            } else if (docSnap.data().status === 'Aktif') {
                batch.update(docSnap.ref, { status: 'Non Aktif' });
            }
        });
        await batch.commit();
    };

    return (
        <MasterDataContext.Provider value={{
            loading,
            kelasRecords, asramaRecords, murobbiRecords, tahunAjaranRecords, activeSemester,
            halaqohRecords, ekskulRecords, divisions, structureMembers,

            fetchTahunAjaranData, fetchKelasData, fetchAsramaData, fetchMurobbiData, fetchHalaqohData, fetchEkskulData, fetchDivisionsData, fetchStructureData,

            addKelas, updateKelas, deleteKelas, deleteMultipleKelas,

            addAsrama, updateAsrama, deleteAsrama, addMultipleAsrama, deleteMultipleAsrama,
            addMurobbi, updateMurobbi, deleteMurobbi, deleteMultipleMurobbi,
            addTahunAjaran, updateTahunAjaran, deleteTahunAjaran, activateTahunAjaran
        }}>
            {children}
        </MasterDataContext.Provider>
    );
};
